import { useQuery } from '@apollo/client';
import { BreadcrumbListItem, Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Pagination } from '@saucelabs/ds-pagination';
import { GroupByDate } from '@saucelabs/sl-component-group-by-date';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { NoData } from '@saucelabs/ds-data-states';
import { IconSearchOutline } from '@saucelabs/ds-icons';

import { graphql } from '../../graphql/__generated__';
import BuildsGuide from './BuildsGuide';
import BuildsItem from './BuildsItem';
import { ContentErrorHandler } from '../common/pages/ContentErrorHandler';
import { BuildFilters } from './BuildFilters';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { selectors, actions } from '../../ducks/filterBar';
import { convertToSec } from '../common/DateLabel';
import { VISUAL_TESTING_BREADCRUMB, VISUAL_ALL_BUILDS_BREADCRUMB } from '../../global/constants';
import useGrowthUserAttribute from '../../hooks/useGrowthUserAttribute';
import { VISUAL_USER_EVENTS } from '../../constants';
import { toLocalDate } from '../../global/utils';

const breadcrumbs: BreadcrumbListItem[] = [
  VISUAL_TESTING_BREADCRUMB,
  { name: VISUAL_ALL_BUILDS_BREADCRUMB.name },
];

const ReadBuilds = graphql(/* GraphQL */ `
  query ReadBuilds (
      $diffOffset: Int!,
      $startDate: Datetime!,
      $endDate: Datetime!,
      $createdByOrgId: [UUID!],
      $createdByUserId: [UUID!],
      $owningTeamId: [UUID!],
      $nameSearchValue: String,
      $status: [BuildStatus!],
      $project: [String!],
      $branch: [String!],
  ) {
    builds (
      offset: $diffOffset,
      first: 50,
      orderBy: CREATED_AT_DESC,
      filter: {
        createdAt: {
          lessThanOrEqualTo: $endDate,
          greaterThanOrEqualTo: $startDate,
        },
        createdByOrgId: {
            in: $createdByOrgId
        },
        createdByUserId: {
            in: $createdByUserId
        },
        owningTeamId: {
            in: $owningTeamId
        },
        fullText: {
            matches: $nameSearchValue
        },
        status: {
            in: $status
        },
        project: {
            in: $project
        },
        branch: {
          in: $branch
        },
      }
    ){
      nodes {
        id
        ...BuildsViewBuildFragment
      }
      totalCount
    }
  }
`);

const Builds: React.FC = () => {
  const dispatch = useVisualDispatch();
  const hasInit = useVisualSelector(selectors.hasInit);
  const { value: selectedOwnerId, type: ownerType } = useVisualSelector(
    selectors.ownerFilterValue,
  );
  const areFiltersApplied = !useVisualSelector(selectors.isDefaultFilters);
  const dateFilterValue = useVisualSelector(selectors.dateFilterValue);
  const nameSearchValue = useVisualSelector(selectors.nameSearchValue);
  const statusValue = useVisualSelector(selectors.statusValue);
  const projectValue = useVisualSelector(selectors.projectValue);
  const branchValue = useVisualSelector(selectors.branchValue);
  const diffOffset = useVisualSelector(selectors.pageValue) - 1;

  const { start, end } = useMemo(() => ({
    start: toLocalDate(dateFilterValue.start),
    end: toLocalDate(dateFilterValue.end.add({ days: 1 })),
  }), [dateFilterValue]);

  const buildsResult = useQuery(ReadBuilds, {
    variables: {
      diffOffset: diffOffset * 50,
      startDate: start,
      endDate: end,
      createdByOrgId: ownerType === 'organization' && selectedOwnerId ? [selectedOwnerId] : null,
      createdByUserId: ownerType === 'user' && selectedOwnerId ? [selectedOwnerId] : null,
      owningTeamId: ownerType === 'team' && selectedOwnerId ? [selectedOwnerId] : null,
      nameSearchValue,
      status: statusValue.length ? statusValue : null,
      project: projectValue,
      branch: branchValue,
    },
    // Skip loading until build filters have initialized. Otherwise, we may fire duplicate calls
    // for default state vs loaded user / saved state
    skip: !hasInit,
    pollInterval: 30 * 1000,
  });
  const builds = buildsResult.data?.builds?.nodes;

  const onPageChange = (page: number) => dispatch(actions.setPage(page));

  useGrowthUserAttribute(VISUAL_USER_EVENTS.hasVisitedVisualBuildIndex);

  return (
    <>
      <Helmet>
        <title>Visual | Builds</title>
      </Helmet>
      <Breadcrumbs list={breadcrumbs} />
      <div
        className="flex flex-col py-2 gap-3"
      >
        <BuildFilters />
        <ContentLoader
          show={buildsResult.loading && !buildsResult.data}
        >
          <ContentErrorHandler
            isServerError={!!buildsResult.error}
          >
            {builds?.length ? (
              <>
                <GroupByDate
                  className="mb-4"
                  datePath={(item) => convertToSec(item.props.build.createdAt)}
                >
                  {builds.map((build) => (
                    <BuildsItem
                      key={build?.id}
                      build={build}
                    />
                  ))}
                </GroupByDate>
                <Pagination
                  className="flex-col"
                  currentPage={diffOffset + 1}
                  itemsPerPage={50}
                  itemsPerPageOptions={[]}
                  onPageChange={onPageChange}
                  totalItems={buildsResult.data?.builds?.totalCount || 0}
                />
              </>
            ) : (
              <>
                {areFiltersApplied ? (
                  <div className="flex justify-center">
                    <NoData
                      icon={IconSearchOutline}
                      spacing="large"
                    />
                  </div>
                ) : <BuildsGuide />}
              </>
            )}
          </ContentErrorHandler>
        </ContentLoader>
      </div>
    </>
  );
};

export default Builds;
